import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    TextField,
    Card,
    CardContent,
    Grid,
    Box,
    InputAdornment,
    IconButton,
    OutlinedInput,
    Link,
    InputLabel,
    FormControl,
    FormControlLabel,
    Switch,
    Dialog,
    DialogContent,
    DialogTitle,
    Checkbox,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import InfoIcon from '@material-ui/icons/Info';
import SaveIcon from '@material-ui/icons/Save';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import CloseIcon from '@material-ui/icons/Close';
import useStyles from './InventoryPage.styles';
import { Link as RouterLink } from 'react-router-dom';
import { routes } from '../../constants.js/misc';
import { useEffect } from 'react';
import {
    addProductInventory,
    addProductInventoryWithVariant,
    downloadStock,
    getInventory,
} from '../../services/inventory';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import FilledCrown from '../../assets/icons/freemium-plan/filled-crown.svg';
import { goToPage, useDebounce } from '../../utils/app';
import LimitedFreemiumPlan from '../../components/LimitedFreemiumPlan';
import ConfirmableButton from '../../components/ConfirmableButton';
import { colors } from '../../constants.js/colors';
import { deleteBulkProduct, updateProductService } from '../../services/products';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ProductImage } from '../ProductsTablePage/ProductsTablePage.styles';

const LOCAL_STORAGE_KEY = 'selectedProducts';

const MobileInventoryPage = ({ storeId, storeData }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [products, setProducts] = useState([]);
    const [gettingData, setGettingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isFreemiumModalOpen, setIsFreemiumModalOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState(() => {
        const saved = localStorage.getItem('selectedProducts');
        return saved ? JSON.parse(saved) : [];
    });

    const isFreemiumPlan = storeData.store_plan.plan.name.toLowerCase() === 'freemium';
    const debouncedSearch = useDebounce(searchQuery, 500);

    // --- Add this above useEffect ---
    const handlePriceChange = (id, newPrice) => {
        setProducts((prev) =>
            prev.map((p) =>
                p.id === id ? { ...p, product: { ...p.product, price: newPrice } } : p
            )
        );
    };

    const handleDiscountChange = (id, newDiscount) => {
        setProducts((prev) =>
            prev.map((p) =>
                p.id === id ? { ...p, product: { ...p.product, discount_price: newDiscount } } : p
            )
        );
    };

    const handleQuantityChange = (id, newQuantity) => {
        setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, quantity: newQuantity } : p)));
    };

    const handleImport = () => {
        if (isFreemiumPlan) return setIsFreemiumModalOpen(true);
        goToPage(routes.inventoryImportPage);
    };

    const handleExport = async () => {
        if (isFreemiumPlan) return setIsFreemiumModalOpen(true);
        await downloadStock(storeId);
    };

    const saveSelectedProduct = async (product) => {
        const payload = {
            quantity: Number(product.quantity),
            price: Number(product.product.price),
            discount_price: Number(product.product.discount_price),
        };

        if (product.product_variant_id === 0) {
            await addProductInventory(storeId, product.product.id, payload);
        } else {
            await addProductInventoryWithVariant(
                storeId,
                product.product.id,
                product.product_variant_id,
                payload
            );
        }
    };

    const loadMore = async () => {
        if (!hasMore) return; // stop if no more data

        setGettingData(true);
        const nextPage = page + 1;

        try {
            const res = await getInventory(
                storeId,
                rowsPerPage,
                nextPage,
                searchQuery.replace(/\+/g, ' ').trim()
            );

            if (res.data && res.data.length > 0) {
                setProducts((prev) => [...prev, ...res.data]);
                setPage(nextPage);
                setHasMore(res.data.length >= rowsPerPage);
            } else {
                setHasMore(false);
            }
        } finally {
            setGettingData(false);
        }
    };

    const toggleProductSelection = (productId) => {
        setSelectedProducts((prev) => {
            const updated = prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId];
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });
    };

    const clearSelections = () => {
        setSelectedProducts([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    };

    const handleRemoveProducts = async () => {
        try {
            await deleteBulkProduct(storeId, selectedProducts);
            enqueueSnackbar('Eliminación exitosa', {
                variant: 'success',
                autoHideDuration: 2500,
            });
        } catch (error) {
            enqueueSnackbar('Error al eliminar productos', {
                variant: 'error',
                autoHideDuration: 2500,
            });
        } finally {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            setSelectedProducts([]);
            window.location.reload();
        }
    };

    const toggleProduct = (selectedProduct) => {
        const product = selectedProduct.product;
        setGettingData(true);
        const newActiveStatus = product.active ? 0 : 1;
        updateProductService(
            storeId,
            {
                active: newActiveStatus,
            },
            product.id,
            true
        )
            .then(() => {
                setGettingData(false);
                setProducts(
                    products.map((p) => ({
                        ...p,
                        product: {
                            ...p.product,
                            active:
                                product.id === p.product.id
                                    ? newActiveStatus
                                    : p.product.active,
                        },
                    }))
                );
                enqueueSnackbar(
                    newActiveStatus
                        ? t('ProductsTable.toggleSuccesActive')
                        : t('ProductsTable.toggleSuccesInactive'),
                    {
                        variant: 'success',
                        autoHideDuration: 2500,
                    }
                );
            })
            .catch((err) => {
                setGettingData(false);
                enqueueSnackbar(
                    newActiveStatus
                        ? t('ProductsTable.toggleErrorActive')
                        : t('ProductsTable.toggleErrorInactive'),
                    {
                        variant: 'error',
                        autoHideDuration: 2500,
                    }
                );
                console.log(err);
            });
    };

    useEffect(() => {
        setGettingData(true);
        setPage(0);

        getInventory(storeId, rowsPerPage, 0, debouncedSearch.replace(/\+/g, ' ').trim())
            .then((res) => {
                setProducts(res.data || []);
                setHasMore(res.data && res.data.length >= rowsPerPage);
            })
            .finally(() => {
                setGettingData(false);
            });
    }, [storeId, rowsPerPage, debouncedSearch]);

    return (
        <Box style={{ background: '#FFFFFF', position: 'relative' }}>
            <div className={classes.header}>
                <Toolbar
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        alignItems: 'flex-start',
                        paddingTop: 8,
                    }}
                >
                    <Typography variant="h6">Inventario</Typography>
                    <div
                        style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                    >
                        <Button
                            variant="outlined"
                            style={{ borderColor: '#173300', color: '#173300' }}
                            onClick={() => {
                                if (isSelectionMode) clearSelections();
                                setIsSelectionMode(!isSelectionMode);
                            }}
                        >
                            {isSelectionMode ? 'Cancelar selección' : 'Selección productos'}
                        </Button>

                        <Link to={routes.productsTable} component={RouterLink}>
                            <Button
                                variant="outlined"
                                style={{ borderColor: '#173300', color: '#173300' }}
                            >
                                Ver productos
                            </Button>
                        </Link>
                    </div>
                </Toolbar>
            </div>

            {/* Search */}
            <div className={classes.section}>
                <TextField
                    fullWidth
                    placeholder="Buscar productos"
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: '#4A4A4A' }} />
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    value={searchQuery}
                />
            </div>

            <div className={`${classes.section} ${classes.actionButtonContainer}`}>
                <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    className={classes.importActionButton}
                    onClick={() => handleImport()}
                >
                    Importar
                    {isFreemiumPlan && (
                        <img className={classes.importFreemiumIcon} src={FilledCrown} />
                    )}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<CloudDownloadIcon />}
                    className={classes.importActionButton}
                    onClick={() => handleExport()}
                >
                    Exportar
                    {isFreemiumPlan && (
                        <img className={classes.importFreemiumIcon} src={FilledCrown} />
                    )}
                </Button>
            </div>

            <Box p={2} style={{ paddingBottom: '120px' }}>
                {gettingData ? (
                    <Typography align="center">Cargando...</Typography>
                ) : products.length === 0 ? (
                    <Typography align="center">No hay productos disponibles</Typography>
                ) : (
                    <>
                        {products.map((product) => (
                            <Card
                                key={product.id}
                                className={classes.card}
                                style={{ marginBottom: 16 }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={0}>
                                        {isSelectionMode && (
                                            <Checkbox
                                                checked={selectedProducts.includes(
                                                    product.product.id
                                                )}
                                                onChange={() =>
                                                    toggleProductSelection(product.product.id)
                                                }
                                                color="primary"
                                            />
                                        )}
                                        <ProductImage style={{ maxWidth: '50px', borderRadius: '8px' }}
                                            src={product?.product?.product_images[0]?.picture_url ? `${process.env.REACT_APP_IMAGES_API_URL}fit-in/600x600/${product?.product?.product_images[0]?.picture_url}` : `https://ui-avatars.com/api/?name=${product?.product?.name}&size=80&background=CAD7CA&color=fff`}
                                            alt={product?.product?.product_images[0]?.name}
                                        />
                                        <Box ml={2} flex={1}>
                                            <Typography variant="subtitle2">
                                                {product.product.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {product.product.sku}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="flex-start" gap={0} mt={2} mb={2}>
                                        <FormControlLabel style={{ margin: 0 }}
                                            control={
                                                <Switch
                                                    disabled={gettingData}
                                                    checked={Boolean(product?.product?.active)}
                                                    onChange={() => toggleProduct(product)}
                                                    name="active"
                                                    size="small"
                                                />
                                            }
                                            label={
                                                product?.product?.active
                                                    ? t('ProductsTable.deactivateProduct')
                                                    : t('ProductsTable.activateProduct')
                                            }
                                        />
                                    </Box>
                                    <Grid container spacing={2} mb={2} mt={2}>
                                        <Grid item xs={6}>
                                            <FormControl variant="outlined" fullWidth size="small">
                                                <InputLabel htmlFor={`normal-price-${product.id}`}>
                                                    Precio normal
                                                </InputLabel>
                                                <OutlinedInput
                                                    id={`normal-price-${product.id}`}
                                                    value={product.product.price}
                                                    onChange={(e) =>
                                                        handlePriceChange(
                                                            product.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    type="number"
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <span>$</span>
                                                        </InputAdornment>
                                                    }
                                                    label="Precio normal"
                                                />
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <FormControl variant="outlined" fullWidth size="small">
                                                <InputLabel
                                                    htmlFor={`discount-price-${product.id}`}
                                                >
                                                    Precio en descuento
                                                </InputLabel>
                                                <OutlinedInput
                                                    id={`discount-price-${product.id}`}
                                                    value={product.product.discount_price}
                                                    onChange={(e) =>
                                                        handleDiscountChange(
                                                            product.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    type="number"
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <span>$</span>
                                                        </InputAdornment>
                                                    }
                                                    label="Precio en descuento"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    <Box display="flex" alignItems="flex-end" gap={16}>
                                        <Box flex={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                Disponible
                                            </Typography>
                                            <Typography variant="body2">
                                                {product.quantity} unidades
                                            </Typography>
                                        </Box>
                                        <Box flex={1}>
                                            <Box display="flex" alignItems="center" mb={0.5}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Editar cantidades
                                                </Typography>
                                                <IconButton size="small">
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <OutlinedInput
                                                type="number"
                                                value={product.quantity}
                                                onChange={(e) =>
                                                    handleQuantityChange(product.id, e.target.value)
                                                }
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        className={classes.saveButton}
                                        startIcon={<SaveIcon />}
                                        onClick={() => saveSelectedProduct(product)}
                                    >
                                        Guardar
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}

                        <Box textAlign="center" mt={2}>
                            {gettingData && products.length > 0 ? (
                                <Typography variant="body2">Cargando más...</Typography>
                            ) : hasMore ? (
                                <Button
                                    variant="outlined"
                                    onClick={loadMore}
                                    disabled={gettingData}
                                >
                                    Cargar más
                                </Button>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No hay más productos
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {isSelectionMode && (
                <Box
                    style={{
                        position: 'fixed',
                        display: 'flex',
                        justifyContent: 'center',
                        bottom: '80px',
                        width: '100%',
                        gap: 8,
                        background: '#FFFFFF',
                        padding: '8px 0',
                        boxShadow: '0 -2px 6px rgba(0,0,0,0.1)',
                        zIndex: 2,
                    }}
                >
                    <ConfirmableButton
                        fullWidth
                        disabled={selectedProducts.length === 0}
                        variant="contained"
                        style={{ backgroundColor: colors.error.main, color: '#FFFFFF' }}
                        confirmMessage={`¿Estás seguro de que deseas eliminar ${selectedProducts.length} productos?`}
                        confirmButtonText="Eliminar"
                        cancelButtonText="Cancelar"
                        onClick={handleRemoveProducts}
                    >
                        Remover {selectedProducts.length} productos
                    </ConfirmableButton>
                </Box>
            )}

            <Dialog
                open={isFreemiumModalOpen}
                onClose={() => setIsFreemiumModalOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    style: {
                        height: '80vh',
                        maxHeight: '90vh',
                    },
                }}
            >
                <DialogTitle
                    disableTypography
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '16px 24px',
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsFreemiumModalOpen(false)}
                        style={{
                            color: '#888',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    style={{
                        overflowY: 'auto',
                        height: '100%',
                    }}
                >
                    <LimitedFreemiumPlan type="import" />
                </DialogContent>
            </Dialog>
        </Box>
    );
};
const mapStateToProps = ({ auth, orders, common }) => ({
    storeId: auth.storeId,
    userEmail: auth.userEmail,
    initialRowsPerPage: orders.rowsPerPage,
    initialPage: orders.page,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MobileInventoryPage);
